/**
 * useReleaseProperties Hook
 *
 * @module features/releases/hooks/useReleaseProperties
 * @description Fetches properties with release QA configs and untested commit counts.
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { throwReleaseApiError } from '../releaseApiError';

export type BuildHead = { id: string; tags: string[]; createdAt: string };

/** A pushed-image application: its timeline is its main builds. */
type BuildPropertyListItem = {
  mode: 'build';
  propertyId: string;
  propertyName: string;
  head: BuildHead | null;
};

type CommitPropertyListItem = {
  mode: 'commit';
  propertyId: string;
  propertyName: string;
  configId: string;
  releaseBranch: string | null;
  untestedCommits: number;
  lastRunId: string | null;
  lastRunStatus: string | null;
  lastRunAt: string | null;
};

export type ReleasePropertyListItem = CommitPropertyListItem | BuildPropertyListItem;

type ReleasePropertiesResponse = {
  ok: boolean;
  properties: ReleasePropertyListItem[];
};

/**
 * Fetch properties with release QA configs.
 */
export function useReleaseProperties() {
  return useQuery({
    queryKey: ['release-qa-properties'],
    queryFn: async (): Promise<ReleasePropertyListItem[]> => {
      const response = await fetch(`${API_URL}/release-qa/properties`, {
        credentials: 'include',
      });

      if (!response.ok) {
        await throwReleaseApiError(response, 'Failed to fetch release properties');
      }

      const data: ReleasePropertiesResponse = await response.json();
      return data.properties;
    },
  });
}
